import { useState, useRef, useEffect, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import AuthContext from "../../auth/AuthContext";
import UserService from "../../services/UserService";
import AlertService from "../../services/AlertService";

import Fqicon from "../../assets/img/Fq.png";

const TwoFactorVerify = () => {
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);

  const [otp, setOtp] = useState(Array(6).fill(""));
  const inputRefs = useRef([]);

  const [qrCode, setQrCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [codeIsValid, setCodeIsValid] = useState(true);

  const validateSetupCompleted = useCallback(() => {
    if (
      authContext.mfaSetupEnabled === false ||
      authContext.mfaSetupCompleted === true
    ) {
      navigate("/");
    }
  }, [authContext, navigate]);

  const loadQrCode = useCallback(async () => {
    try {
      setLoading(true);
      const result = await UserService.GetQrCode(authContext);

      if (result.isSuccess) {
        setQrCode(result.data.qrCodeSetupImageUrl);
      } else {
        AlertService.error(result.errorMessage);
      }
    } catch (err) {
      console.error(err);
      AlertService.error("Failed to load QR code");
    } finally {
      setLoading(false);
    }
  }, [authContext]);

  useEffect(() => {
    validateSetupCompleted();
    loadQrCode();
    inputRefs.current[0]?.focus();
  }, [validateSetupCompleted, loadQrCode]);

  /* ================= OTP INPUT ================= */
  const handleChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      if (!otp[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    }

    if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    if (e.key === "ArrowRight" && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData.getData("text").slice(0, 6);
    if (!/^\d+$/.test(paste)) return;

    const newOtp = paste.split("");
    setOtp([...newOtp, ...Array(6 - newOtp.length).fill("")]);
    inputRefs.current[Math.min(paste.length - 1, 5)]?.focus();
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    const finalOtp = otp.join("");

    if (finalOtp.length < 6) {
      setCodeIsValid(false);
      return;
    }

    try {
      setLoading(true);
      const result = await UserService.VerifyQrCode(finalOtp, authContext);

      if (result.isSuccess) {

          navigate("/login");
      } else {
        AlertService.error(result.errorMessage);
      }
    } catch (err) {
      console.error(err);
      AlertService.error("Verification failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex font-sans">

      {/* LEFT PANEL */}
      <div className="hidden md:flex w-1/2 bg-[#0e3a46] relative overflow-hidden">
        <div className="absolute top-12 -right-10 bg-white h-24 w-56 rounded-full flex items-center pl-12 shadow-md">
          <img src={Fqicon} alt="Logo" className="h-12" />
        </div>
      </div>

      {/* RIGHT PANEL */}
      <div className="flex w-full md:w-1/2 items-center justify-center bg-white px-4">
        <form
          onSubmit={handleVerify}
          onPaste={handlePaste}
          className="w-full max-w-[420px] text-center"
        >
          <h2 className="text-2xl font-semibold mb-3">
            Two Factor Verification Setup
          </h2>

          <p className="text-base text-gray-600 mb-4">
            Please scan the QR code using Microsoft Authenticator or any
            authenticator app.
          </p>

          {/* QR CODE */}
          {qrCode && (
            <div className="flex justify-center mb-4">
              <img
                src={qrCode}
                alt="QR Code"
                className="w-44 h-44 border p-2 rounded"
              />
            </div>
          )}

          <p className="text-base text-gray-600 mb-4">
            After scanning, enter the 6-digit code generated by your
            authenticator app.
          </p>

          {/* OTP INPUTS */}
          <div className="flex justify-center gap-3 mb-4">
            {otp.map((digit, i) => (
              <input
                key={i}
                ref={(el) => (inputRefs.current[i] = el)}
                value={digit}
                maxLength={1}
                inputMode="numeric"
                disabled={loading}
                onChange={(e) => handleChange(e.target.value, i)}
                onKeyDown={(e) => handleKeyDown(e, i)}
                onFocus={(e) => e.target.select()}
                autoFocus={i === 0} 
                className="
                  w-12 h-12
                  border border-gray-300
                  rounded-md
                  text-center text-lg
                  outline-none
                  focus:ring-2 focus:ring-[#0e3a46]
                "
              />
            ))}
          </div>

          {!codeIsValid && (
            <p className="text-red-500 text-sm mb-3">
              Please enter a valid 6-digit code
            </p>
          )}

          {/* VERIFY */}
          <button
            type="submit"
            disabled={loading}
            className={`
              w-full h-12
              bg-[#0e3a46]
              text-white
              rounded-md
              font-medium
              hover:opacity-95
              ${loading && "opacity-50 cursor-not-allowed"}
            `}
          >
            {loading ? "Processing..." : "Verify & Complete Setup"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default TwoFactorVerify;